import React, { useState, useEffect } from 'react';
import { getAdminUnitTree, deleteAdminUnit } from '../../../services/adminUnits';
import Modal from '../../../components/Common/Modal';
import AdminUnitForm from './AdminUnitForm';
import Toast from '../../../components/Common/Toast';
import { 
  ChevronRight, 
  ChevronDown, 
  FolderPlus, 
  Trash2, 
  Plus, 
  Layers, 
  Network,
  Search,
  AlertCircle
} from 'lucide-react';

const AdminUnitTree = () => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [parentId, setParentId] = useState(null);
  const [toast, setToast] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    loadTree();
  }, []);

  const loadTree = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminUnitTree();
      // Ensure data is an array
      setTree(Array.isArray(data) ? data : []);
      if (Array.isArray(data) && data.length === 0) {
        setError('No admin units found.');
      }
    } catch (err) {
      console.error('Failed to load admin units:', err);
      setError(err.response?.data?.error || 'Failed to load structure. Please try again.');
      setToast({ type: 'error', message: 'Could not load admin units' });
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure? Deleting "${name}" will remove all sub-units.`)) {
      try {
        await deleteAdminUnit(id);
        setToast({ type: 'success', message: 'Unit removed successfully' });
        loadTree(); // refresh
      } catch (err) {
        setToast({ type: 'error', message: err.response?.data?.error || 'Delete failed' });
      }
    }
  };

  const renderNode = (node, depth = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded[node.id];

    return (
      <div key={node.id} className="admin-tree-node">
        <div 
          className={`group flex items-center justify-between p-3 rounded-xl transition-all mb-1 ${
            depth === 0 ? 'bg-white border border-slate-200 shadow-sm' : 'hover:bg-slate-100/50'
          }`}
          style={{ marginLeft: `${depth * 24}px` }}
        >
          <div className="flex items-center gap-3 overflow-hidden">
            <button 
              onClick={() => toggleNode(node.id)}
              className={`p-1 rounded-md hover:bg-slate-200 transition-colors ${!hasChildren && 'invisible'}`}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>

            <div className={`p-2 rounded-lg ${depth === 0 ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
              <Layers size={14} />
            </div>

            <div className="flex flex-col min-w-0">
              <span className={`truncate font-bold text-sm ${depth === 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                {node.name}
              </span>
              <span className="text-[10px] font-mono text-slate-400 uppercase">
                {node.code} • Level {node.level}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button 
              onClick={() => { setParentId(node.id); setShowModal(true); }}
              className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
              title="Add Sub-unit"
            >
              <FolderPlus size={16} />
            </button>
            <button 
              onClick={() => handleDelete(node.id, node.name)}
              className="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg"
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="tree-branch relative">
            <div 
              className="absolute left-[15px] top-0 bottom-0 w-[1px] bg-slate-200" 
              style={{ marginLeft: `${depth * 24}px` }}
            />
            {node.children.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const handleOpenRootModal = () => {
    setParentId(null);
    setShowModal(true);
  };

  const handleFormSuccess = () => {
    loadTree();
    setShowModal(false);
    setParentId(null);
  };

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto space-y-8">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-slate-200 pb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Network size={16} className="text-indigo-600" />
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Governance Shell</span>
          </div>
          <h1 className="text-3xl font-black text-slate-900">Administrative Units</h1>
          <p className="text-slate-500 text-sm">Manage the geographic hierarchy of your organization.</p>
        </div>
        
        <button 
          onClick={handleOpenRootModal}
          className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-2xl font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all active:scale-95"
        >
          <Plus size={20} strokeWidth={3} />
          <span>Add Root Unit</span>
        </button>
      </div>

      {/* SEARCH BAR (placeholder) */}
      <div className="bg-slate-100 p-2 rounded-2xl mb-6 border border-slate-200/50">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input 
            type="text" 
            placeholder="Filter units..." 
            className="w-full pl-11 pr-4 py-2 bg-transparent border-none text-sm focus:ring-0" 
          />
        </div>
      </div>

      {/* CONTENT */}
      <div className="tree-viewport">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner" />
          </div>
        ) : error ? (
          <div className="text-center py-20 border-2 border-dashed border-red-200 rounded-[2rem] bg-red-50">
            <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
            <p className="text-red-600 font-bold">{error}</p>
            <button 
              onClick={loadTree}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Retry
            </button>
          </div>
        ) : tree.length === 0 ? (
          <div className="text-center py-20 border-2 border-dashed border-slate-200 rounded-[2rem]">
            <Layers size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 font-medium">No units found. Click "Add Root Unit" to begin.</p>
          </div>
        ) : (
          <div className="space-y-1">
            {tree.map(node => renderNode(node))}
          </div>
        )}
      </div>

      {/* MODAL */}
      <Modal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={parentId ? "Add Sub-Unit" : "Add New Root Unit"}
      >
        <div className="modal-content-inner p-4">
          {parentId && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-100 rounded-xl text-[11px] text-amber-700 font-bold uppercase tracking-tight">
              Adding child to ID: {parentId}
            </div>
          )}
          <AdminUnitForm 
            key={parentId || 'root-form'} 
            parentId={parentId} 
            onSuccess={handleFormSuccess}
          />
        </div>
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default AdminUnitTree;